import { useEffect, useState, useCallback, useRef } from 'react';
import { authHeaders, _stillLoadingToast, _dataReadyToast } from '../utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useMapData(datasetIds, selectedYears = []) {
  const [locations, setLocations] = useState([]);
  const [fallbackLocations, setFallbackLocations] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [approximateCount, setApproximateCount] = useState(0);
  const [geocodingPending, setGeocodingPending] = useState(0);
  const [geocodingActive, setGeocodingActive] = useState(false);
  const [geocodedCount, setGeocodedCount] = useState(0);
  const [totalAddresses, setTotalAddresses] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [fitKey, setFitKey] = useState(0);
  const [geocodingSticky, setGeocodingSticky] = useState(false);
  const abortRef = useRef(null);
  const retryTimerRef = useRef(null);  // Scheduled retry timer — cancelled on dataset change
  const fetchTokenRef = useRef(0);     // Monotonic token — stale responses can't touch state
  const hasRefreshedRef = useRef(false);
  const geocodingWasActiveRef = useRef(false);
  const geocodingGraceTimerRef = useRef(null);
  const hadLocationsRef = useRef(false);

  // Build years query string
  const yearsQs = selectedYears.length > 0 ? `&years=${selectedYears.join(',')}` : '';

  // Fetch a single dataset's location data
  const fetchSingleDataset = async (dsId, signal, forceRefresh = false) => {
    const qs = forceRefresh ? `&_refresh=${Date.now()}` : '';
    const response = await fetch(
      `${API}/charts/location-clusters?dataset_ids=${dsId}${qs}${yearsQs}`,
      { signal, headers: authHeaders() }
    );
    // Trigger the still-loading toast handler on 503 without changing the
    // existing throw/retry behaviour the hook already relies on.
    if (response.status === 503) {
      try {
        const body = await response.clone().json();
        if (body && body.error === 'still_loading') {
          _stillLoadingToast(body.message);
        }
      } catch (_) { /* ignore */ }
    } else if (response.ok) {
      _dataReadyToast();
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const fetchLocations = useCallback(async (silent = false, retryNum = 0, geocodingRefresh = false) => {
    // On a fresh call (not a scheduled retry), invalidate any in-flight
    // fetch AND any pending retry from a previous dataset selection.
    // Without this, a scheduled retry from the OLD selection would fire
    // 3-9s later, hit the OLD dataset endpoint, and its response would
    // overwrite the new selection's state — the user perceives this as
    // "switching datasets stalls out" because pins for the wrong folder
    // paint in and the loading spinner bounces.
    if (retryNum === 0) {
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* ignore */ } }
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      fetchTokenRef.current += 1;
    }
    const myToken = fetchTokenRef.current;
    if (!datasetIds) {
      setLocations([]);
      setFallbackLocations([]);
      setInitialLoading(false);
      setMapCenter(null);
      setGeocodingPending(0);
      setGeocodedCount(0);
      setConfirmedCount(0);
      setApproximateCount(0);
      setTotalAddresses(0);
      hadLocationsRef.current = false;
      return;
    }
    if (!silent) setInitialLoading(true);
    else setRefreshing(true);
    const controller = new AbortController();
    abortRef.current = controller;

    const ids = datasetIds.split(',').map(s => s.trim()).filter(Boolean);
    // Re-fit map bounds on initial load/dataset change, or when first pins appear during geocoding
    const isInitialOrDatasetChange = !silent && !geocodingRefresh;

    try {
      const timeout = setTimeout(() => controller.abort(), 180000);

      let mergedData;

      // Send ALL selected dataset IDs in ONE request so the backend's
      // per-dataset parallelization + retry logic actually kicks in.
      // Previously we sent 10 separate HTTP calls (one per dataset) and
      // silently swallowed any that failed with `.catch(err => null)` —
      // that's why production maps intermittently showed only "42
      // street-level" for a 12k-address folder. One big request is now
      // handled by the backend with 3-way parallelism, 90s retry, and
      // proper 503 on failure so the amber "still loading" toast fires
      // rather than silently returning partial data.
      if (ids.length === 1) {
        const data = await fetchSingleDataset(ids[0], controller.signal, geocodingRefresh);
        mergedData = Array.isArray(data) ? { locations: data, fallback_locations: [] } : data;
      } else {
        const combinedId = ids.join(',');
        const data = await fetchSingleDataset(combinedId, controller.signal, geocodingRefresh);
        mergedData = Array.isArray(data) ? { locations: data, fallback_locations: [] } : data;
      }

      clearTimeout(timeout);

      // Stale-response guard: if the user switched datasets while this
      // fetch was in-flight, `fetchTokenRef.current` will have been
      // incremented by the new fetch. Silently discard our result.
      if (myToken !== fetchTokenRef.current) return;

      if (!controller.signal.aborted && mergedData) {
        const newLocations = mergedData.locations || [];
        const newFallback = mergedData.fallback_locations || [];
        if (geocodingRefresh) {
          setLocations(prev => newLocations.length >= prev.length ? newLocations : prev);
          setFallbackLocations(prev => newFallback.length >= prev.length ? newFallback : prev);
        } else {
          setLocations(newLocations);
          setFallbackLocations(newFallback);
        }
        if (mergedData.center) setMapCenter(mergedData.center);
        const totalPinCount = (mergedData.locations?.length || 0) + (mergedData.fallback_locations?.length || 0);
        const firstPins = !hadLocationsRef.current && totalPinCount > 0;
        if (isInitialOrDatasetChange || firstPins) {
          hadLocationsRef.current = true;
          setFitKey(k => k + 1);
        }
        setConfirmedCount(prev => {
          const newVal = mergedData.confirmed_count || 0;
          return (geocodingRefresh && newVal < prev) ? prev : newVal;
        });
        setApproximateCount(prev => {
          const newVal = mergedData.approximate_count || 0;
          return (geocodingRefresh && newVal < prev) ? prev : newVal;
        });
        setGeocodingPending(mergedData.geocoding_pending || 0);
        setGeocodedCount(mergedData.geocoded_count || 0);
        setTotalAddresses(mergedData.total_addresses || 0);
        setGeocodingActive(!!mergedData.geocoding_active);
        setInitialLoading(false);
        setRefreshing(false);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Stale error from a superseded fetch — ignore.
      if (myToken !== fetchTokenRef.current) return;
      console.error('Failed to fetch locations:', error);
      setRefreshing(false);
      if (retryNum < 2) {
        retryTimerRef.current = setTimeout(() => {
          // Double-check we're still the current fetch before actually retrying.
          if (myToken === fetchTokenRef.current) {
            retryTimerRef.current = null;
            fetchLocations(silent, retryNum + 1, geocodingRefresh);
          }
        }, 3000 * (retryNum + 1));
      }
      // If retries are exhausted we deliberately KEEP initialLoading=true.
      // The ProgressPanel stays visible with the "Retry Now" button so
      // the user has a clear escape hatch, and the backend's background
      // finisher (spawned on 503) may complete the per-dataset cache in
      // the next ~30s — at which point a manual retry succeeds instantly.
    }
  }, [datasetIds, yearsQs]);

  const fetchGeocodingStatus = useCallback(async () => {
    if (!datasetIds) {
      setGeocodingPending(0);
      setGeocodedCount(0);
      setConfirmedCount(0);
      setApproximateCount(0);
      setTotalAddresses(0);
      setGeocodingActive(false);
      return;
    }
    try {
      const response = await fetch(`${API}/geocoding-status?dataset_ids=${datasetIds}`, { headers: authHeaders() });
      const data = await response.json();
      const incomingTotal = data.total_addresses || 0;
      const incomingConfirmed = data.confirmed_count || 0;
      const incomingApprox = data.approximate_count || 0;
      const incomingPending = data.geocoding_pending || 0;
      const hasSignal = incomingConfirmed > 0 || incomingApprox > 0 || incomingPending > 0 || incomingTotal > 0;

      // Only accept updates that look real. A transient `total_addresses=0`
      // response (status endpoint racing the geocode_stats cache, or a
      // dataset selection change still propagating) was causing the
      // progress bar to disappear and immediately reappear on the next
      // poll. Skipping these transient empties keeps the bar stable.
      setTotalAddresses(prev => (incomingTotal > 0 ? incomingTotal : prev));
      if (hasSignal) {
        setGeocodingPending(incomingPending);
        setGeocodedCount(data.geocoded_count || 0);
        setConfirmedCount(incomingConfirmed);
        setApproximateCount(incomingApprox);
      }
      setGeocodingActive(!!data.geocoding_active);

      // Sticky geocoding: keep progress bar visible with grace period
      const isCurrentlyActive = !!data.geocoding_active || (data.geocoding_pending || 0) > 0;
      if (isCurrentlyActive) {
        geocodingWasActiveRef.current = true;
        setGeocodingSticky(true);
        if (geocodingGraceTimerRef.current) {
          clearTimeout(geocodingGraceTimerRef.current);
          geocodingGraceTimerRef.current = null;
        }
      } else if (geocodingWasActiveRef.current && !geocodingGraceTimerRef.current) {
        // Geocoding appears done — wait 60s grace period before hiding
        geocodingGraceTimerRef.current = setTimeout(() => {
          geocodingWasActiveRef.current = false;
          setGeocodingSticky(false);
          geocodingGraceTimerRef.current = null;
        }, 60000);
      }
    } catch (error) {
      console.error('Failed to fetch geocoding status:', error);
    }
  }, [datasetIds]);

  // Initial fetch + reset on dataset change
  useEffect(() => {
    hasRefreshedRef.current = false;
    geocodingWasActiveRef.current = false;
    setGeocodingSticky(false);
    if (geocodingGraceTimerRef.current) {
      clearTimeout(geocodingGraceTimerRef.current);
      geocodingGraceTimerRef.current = null;
    }
    setMapCenter(null);
    setLocations([]);
    setFallbackLocations([]);
    setFitKey(0);
    fetchLocations(false);
    fetchGeocodingStatus();
  }, [fetchLocations, fetchGeocodingStatus]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchLocations(true, 0, true), fetchGeocodingStatus()]);
    setRefreshing(false);
  };

  // Auto-refresh when a committee is tagged/untagged from the Party Audit
  // panel. The backend clears `map_clusters_cache` on tag mutation, so we
  // silently re-fetch to pick up new party colors without a manual click.
  useEffect(() => {
    const onPartyTagChanged = () => {
      fetchLocations(true, 0, true);
    };
    window.addEventListener("party-tag-changed", onPartyTagChanged);
    return () => window.removeEventListener("party-tag-changed", onPartyTagChanged);
  }, [fetchLocations]);

  return {
    locations,
    fallbackLocations,
    confirmedCount,
    approximateCount,
    geocodingPending,
    geocodingActive,
    geocodingSticky,
    geocodedCount,
    totalAddresses,
    initialLoading,
    refreshing,
    mapCenter,
    fitKey,
    fetchLocations,
    fetchGeocodingStatus,
    handleRefresh,
  };
}
